import { forwardRef, ImgHTMLAttributes, useEffect, useState } from "react";

import { shouldStretchToContainer } from "../../utils/video";

interface LoadedImage {
    /** The src to render into the visible <img>. For fetched URLs this is a blob object URL. */
    src: string;
    aspectRatio: number;
}

// `<img>` that stretches to fill its container (via `object-fit: fill`) when the source's
// natural aspect ratio is ~8:9 (and maybe others added later).
// The source is fetched and decoded once; only after that do we render the visible <img>
// (using a blob URL for fetched sources) so its first paint already has the correct stretch style.
const StretchAwareImage = forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement>>(
    ({ src, style, ...props }, ref) => {
        const [loaded, setLoaded] = useState<LoadedImage>();

        useEffect(() => {
            setLoaded(undefined);
            if (!src) return;

            let cancelled = false;
            let objectUrl: string | undefined;

            const measure = (imgSrc: string) => {
                const img = new Image();
                img.onload = () => {
                    if (cancelled || !img.naturalWidth || !img.naturalHeight) {
                        return;
                    }
                    setLoaded({
                        src: imgSrc,
                        aspectRatio: img.naturalWidth / img.naturalHeight,
                    });
                };
                img.onerror = () => {
                    // Fall back to the original src so the visible <img> can still surface its own error.
                    if (!cancelled) {
                        setLoaded({ src, aspectRatio: 0 });
                    }
                };
                img.src = imgSrc;
            };

            // Data URIs don't need fetching — decode directly.
            if (src.startsWith('data:')) {
                measure(src);
            } else {
                fetch(src)
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`Failed to load image (${response.status})`);
                        }
                        return response.blob();
                    })
                    .then((blob) => {
                        if (cancelled) return;
                        objectUrl = URL.createObjectURL(blob);
                        measure(objectUrl);
                    })
                    .catch(() => {
                        if (!cancelled) {
                            setLoaded({ src, aspectRatio: 0 });
                        }
                    });
            }

            return () => {
                cancelled = true;
                if (objectUrl) URL.revokeObjectURL(objectUrl);
            };
        }, [src]);

        if (src && !loaded) {
            return null;
        }

        const stretch = shouldStretchToContainer(loaded?.aspectRatio);

        return (
            <img
                ref={ref}
                src={loaded?.src ?? src}
                alt=""
                {...props}
                style={
                    stretch
                        ? { aspectRatio: '16 / 9', ...style, objectFit: 'fill' }
                        : style
                }
            />
        );
    }
);

StretchAwareImage.displayName = "StretchAwareImage";

export default StretchAwareImage;
